import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import AOS from 'aos';
import 'aos/dist/aos.css';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ThemeProvider } from "./context/ThemeContext";
import axios from "axios";
import { CONFIG } from "./utils/config";

// Components
import Navigation from "./components/Navigation";
import MainTitle from "./components/MainTitle";
import Crypto3DScroller from "./components/Crypto3DScroller";
import CryptoTracker from "./components/CryptoTracker";
import EnhancedRateCalculator from "./components/EnhancedRateCalculator";
import WhatsAppModal from "./components/WhatsAppModal";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import ScrollToTop from "./components/ScrollToTop";
import LanguageSwitcher from "./components/LanguageSwitcher";
import TransactionHistory from "./components/TransactionHistory";
import FounderSection from "./components/FounderSection";
import FAQAccordion from "./components/FAQAccordion";
import ChannelCTA from "./components/ChannelCTA";
import PartnersScroller from "./components/PartnersScroller";
import MouseGlow from "./components/MouseGlow";
import Testimonials from "./components/Testimonials";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import TrustSection from "./components/TrustSection";
import HomeBlogSection from "./components/HomeBlogSection";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Legal from "./pages/Legal";
import NotFound from "./pages/NotFound";

import { Trans, useTranslation } from 'react-i18next';

// Features
const HowItWorks = () => {
    const { t } = useTranslation();
    return (
    <section id="how-it-works" className="container py-24">
        <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-primary glow-text" data-aos="fade-up">{t('howItWorks.title')}</h2>
            <p className="text-text-muted text-lg max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="100">
                {t('howItWorks.subtitle')}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent -z-10"></div>

                { [
                    { step: "01", title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.desc') },
                    { step: "02", title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.desc') },
                    { step: "03", title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.desc') }
                ].map((item, i) => (
                    <div key={i} className="glass p-10 relative overflow-hidden group hover:border-black/20 dark:hover:border-white/20 transition-all duration-300" data-aos="fade-up" data-aos-delay={i * 200}>
                        <div className="absolute top-0 right-0 p-4 font-black text-6xl text-black/5 dark:text-white/5 group-hover:text-primary transition-colors">
                            {item.step}
                        </div>
                        <h3 className="text-2xl font-bold mb-4 relative z-10 text-text-main">{item.title}</h3>
                        <p className="text-text-muted leading-relaxed relative z-10">{item.desc}</p>
                        <div className="mt-6 w-12 h-1 bg-primary group-hover:w-full transition-all duration-500"></div>
                    </div>
                ))}
            </div>
    </section>
    );
};

const WhyChooseUs = () => {
    const { t } = useTranslation();
    const [trades, setTrades] = React.useState([
        { id: "X7A", amount: 500 },
        { id: "F9B", amount: 1250 },
        { id: "4C2", amount: 300 },
        { id: "9P1", amount: 840 }
    ]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setTrades(prevTrades => {
                const newTrade = {
                    id: Math.random().toString(36).substring(2, 5).toUpperCase(),
                    amount: Math.floor(Math.random() * (3000 - 50 + 1) + 50)
                };
                return [newTrade, ...prevTrades.slice(0, 3)];
            });
        }, 4500);
        return () => clearInterval(interval);
    }, []);

    return (
        <section id="why-us" className="relative overflow-hidden py-24 bg-black border-y border-glass-border optimize-gpu">
            <div className="bg-grid"></div>
            <div className="container relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight" data-aos="fade-right">
                            <Trans i18nKey="whyChooseUs.title">
                                Trusted by <span className="text-primary italic">100k+</span> Satisfied Users
                            </Trans>
                        </h2>
                        <div className="space-y-6">
                            {[
                                { icon: "⚡", title: t('whyChooseUs.feature1.title'), desc: t('whyChooseUs.feature1.desc') },
                                { icon: "🛡️", title: t('whyChooseUs.feature2.title'), desc: t('whyChooseUs.feature2.desc') },
                                { icon: "💎", title: t('whyChooseUs.feature3.title'), desc: t('whyChooseUs.feature3.desc') }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-6 items-start" data-aos="fade-right" data-aos-delay={i * 150}>
                                    <div className="w-14 h-14 glass flex items-center justify-center text-3xl shrink-0 border-primary/30 shadow-lg">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold mb-1">{item.title}</h4>
                                        <p className="text-text-muted">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                            <button 
                                onClick={() => window.scrollTo({ top: document.getElementById('calculator').offsetTop - 100, behavior: 'smooth' })}
                                className="relative group px-14 py-6 bg-[#00ff88] text-black font-black text-xs md:text-sm uppercase tracking-[0.3em] rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,255,136,0.6)] hover:shadow-[0_0_50px_rgba(0,255,136,0.8)] transition-all active:scale-95 animate-pulse-whatsapp"
                            >
                                <span className="relative z-10 flex items-center gap-3">
                                    {t('whyChooseUs.btnCheckRates')}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-right group-hover:translate-x-1 transition-transform" aria-hidden="true">
                                        <path d="m9 18 6-6-6-6"></path>
                                    </svg>
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            </button>
                        </div>
                    </div>
                    <div className="relative" data-aos="zoom-in">
                        <div className="glass p-8 rotate-3 hover:rotate-0 transition-transform duration-500 relative z-10">
                            <div className="flex justify-between items-center mb-6">
                                <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">{t('whyChooseUs.ledgerTitle')}</span>
                                <span className="text-text-muted text-xs animate-pulse">{t('whyChooseUs.ledgerSub')}</span>
                            </div>
                            <div className="space-y-4">
                                {trades.map((trade, idx) => (
                                    <div key={trade.id + idx} className="flex justify-between items-center p-3 border-b border-white/5">
                                        <div className="flex gap-3 items-center">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary/80"></div>
                                            <div>
                                                <div className="text-sm font-bold">TX #ZAPTO...{trade.id}</div>
                                                <div className="text-[10px] text-text-muted">{t('whyChooseUs.ledgerSuccess')}</div>
                                            </div>
                                        </div>
                                        <div className="text-primary font-mono text-sm">+${trade.amount.toFixed(2)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};



function AppContent() {
    const { t } = useTranslation();
    const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = React.useState(false);
    const [whatsAppMessage, setWhatsAppMessage] = React.useState("");
    const [rates, setRates] = React.useState({ sell: 580, buy: 605 });
    const location = useLocation();

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const res = await axios.get(`${CONFIG.API_BASE}/rates/current?currency=USDT`, {
                    headers: { 'Cache-Control': 'no-cache' }
                });
                if (res.data) {
                    setRates({
                        sell: res.data.sell_rate || 580,
                        buy: res.data.buy_rate || 605
                    });
                }
            } catch (error) {
                console.error('[App] Failed to fetch current rates:', error);
            }
        };
        fetchRates();
        const interval = setInterval(fetchRates, 30000); // sync every 30s
        return () => clearInterval(interval);
    }, []);

    const openWhatsAppModal = (message = "") => {
        const customMessage = typeof message === 'string' ? message : "";
        const defaultMsg = `👋 Hello Zaptopay! I'm interested in trading crypto for XAF. I see the current live rates are We Buy: ${rates.sell} XAF / We Sell: ${rates.buy} XAF. Please guide me through the process.`;
        setWhatsAppMessage(customMessage || defaultMsg);
        setIsWhatsAppModalOpen(true);
    };

    useEffect(() => {
        const hash = location.hash;
        if (hash) {
            setTimeout(() => {
                // Handle special cases
                let targetId = hash.substring(1);
                if (targetId === "market") targetId = "market-pulse";
                
                const el = document.getElementById(targetId);
                if (el) {
                    window.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
                }
            }, 150);
        } else if (location.pathname === "/") {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [location.pathname, location.hash]);

    useEffect(() => {
        AOS.init({
            duration: 1000,
            once: true,
            easing: 'ease-out-cubic',
        });
    }, []);

    const orgSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Zaptopay",
        "url": "https://zaptopay.online",
        "logo": "https://zaptopay.online/assets/logos/zaptopay-og.png",
        "description": "Fastest and most secure Crypto-to-XAF exchange in Cameroon. Sell Bitcoin, Ethereum, and USDT instantly."
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "How fast is a Zaptopay transaction?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Average transaction time is under 2 minutes. Your XAF is sent via Mobile Money immediately upon blockchain confirmation."
                }
            },
            {
                "@type": "Question",
                "name": "Is Zaptopay secure?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Zaptopay uses end-to-end encrypted trade processes and verified agent matching for all crypto-to-XAF exchanges."
                }
            }
        ]
    };

    return (
        <div className="App text-text-main selection:bg-white selection:text-black group overflow-x-hidden w-full flex flex-col min-h-screen">
            <MouseGlow />
            <Helmet>
                <title>Zaptopay | Secure Crypto-to-XAF Exchange Cameroon</title>
                <meta name="description" content="Exchange Bitcoin, USDT, and Ethereum for XAF instantly. The most trusted crypto exchange in Central Africa with live marketplace rates and verified security." />
                <meta name="keywords" content="crypto to xaf, sell usdt cameroon, bitcoin to mobile money, zaptopay, crypto exchange africa, douala crypto, yaounde bitcoin" />
                <link rel="canonical" href="https://zaptopay.online/" />
                <meta property="og:locale" content="en_CM" />
                <meta property="og:locale:alternate" content="fr_CM" />
                
                <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Zaptopay Cameroon",
                    "image": "https://zaptopay.online/assets/logos/zaptopay-og.png",
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Douala",
                        "addressRegion": "Littoral",
                        "addressCountry": "CM"
                    },
                    "url": "https://zaptopay.online",
                    "telephone": "+237600000000",
                    "priceRange": "$$",
                    "openingHoursSpecification": {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": [
                            "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
                        ],
                        "opens": "00:00",
                        "closes": "23:59"
                    }
                })}</script>
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Service",
                    "serviceType": "Cryptocurrency Exchange",
                    "provider": {
                        "@type": "LocalBusiness",
                        "name": "Zaptopay"
                    },
                    "areaServed": {
                        "@type": "Country",
                        "name": "Cameroon"
                    },
                    "hasOfferCatalog": {
                        "@type": "OfferCatalog",
                        "name": "Crypto to XAF Exchange",
                        "itemListElement": [
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Service",
                                    "name": "USDT to XAF Instant Payout"
                                }
                            },
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Service",
                                    "name": "Bitcoin to XAF Instant Payout"
                                }
                            }
                        ]
                    }
                })}</script>
            </Helmet>

            <Navigation onTradeClick={() => openWhatsAppModal()} />
            <Routes>
                <Route path="/" element={
                    <main>
                        <section className="scroll-gpu">
                            <MainTitle onTradeClick={openWhatsAppModal} />
                        </section>

                        {/* Enhanced Calculator Section */}
                        <EnhancedRateCalculator onWhatsAppClick={openWhatsAppModal} />

                        {/* Market Pulse Section */}
                        <section id="market-pulse" className="container relative z-10 py-16">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase tracking-tighter" data-aos="fade-up">{t('marketPulse.title')}</h2>
                                <p className="text-text-muted text-lg" data-aos="fade-up" data-aos-delay="100">{t('marketPulse.subtitle')}</p>
                            </div>
                            <div className="max-w-6xl mx-auto" data-aos="zoom-in">
                                <CryptoTracker />
                            </div>
                        </section>

                        <Crypto3DScroller />
                        <PartnersScroller />
                        <HowItWorks />
                        <WhyChooseUs />
                        <TrustSection />

                        <TransactionHistory />
                        <Testimonials />
                        <ChannelCTA />
                        <FounderSection />
                        <HomeBlogSection />
                        <FAQAccordion />
                        <ContactSection />
                        <Footer />
                    </main>
                } />
                <Route path="/legal" element={<Legal />} />
                <Route path="/terms" element={<Navigate to="/legal?tab=terms" replace />} />
                <Route path="/privacy" element={<Navigate to="/legal?tab=privacy" replace />} />
                <Route path="/aml-cft" element={<Navigate to="/legal?tab=aml-cft" replace />} />
                <Route path="/risk-disclosure" element={<Navigate to="/legal?tab=risk" replace />} />
                <Route path="/cookies" element={<Navigate to="/legal?tab=cookies" replace />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route 
                    path="/admin/dashboard" 
                    element={
                        <ProtectedRoute>
                            <AdminDashboard />
                        </ProtectedRoute>
                    } 
                />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <ScrollToTop />
            <LanguageSwitcher />
            <FloatingWhatsApp onClick={() => openWhatsAppModal()} />
            <WhatsAppModal 
                isOpen={isWhatsAppModalOpen}
                onClose={() => setIsWhatsAppModalOpen(false)}
                initialMessage={whatsAppMessage}
            />
        </div>
    );
}

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider>
                <BrowserRouter>
                    <AppContent />
                </BrowserRouter>
            </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;